import React, { useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import Box from '@material-ui/core/Box';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';

import FullPageLayout from '../_layout/FullPageLayout/full-page.layout';
import LoadingPage from '../_boilerplate/LoadingPage/loading-page.container';
import { useParentPlayers, useParentAccountConflicts } from '../../../lib/services/parent.service';
import { IParentAccountConflict, IParentMaskedCredential } from '../../../lib/types';
import { ParentAccountConflictBanner } from '../../User/ParentAccountConflict/parent-account-conflict-banner.component';
import { ParentAccountSwitchDialog } from '../../User/ParentAccountConflict/parent-account-switch-dialog.container';
import { ParentPlayerCard } from './parent-player-group-card.component';
import { ParentDashboardEmptyState } from './parent-dashboard-empty-state.component';
import { flattenGroups, sortPlayers } from './parent-dashboard.utils';

// URL query key for the show-past-teams toggle; kept in the URL so the state
// survives back/forward + report-card round-trips and stays bookmarkable.
const PAST_TEAMS_PARAM = 'past';

export const ParentDashboardPage: React.FC = () => {
  const { groups, loading } = useParentPlayers();
  const { conflicts } = useParentAccountConflicts();
  const [switching, setSwitching] = useState<{
    conflict: IParentAccountConflict;
    credential: IParentMaskedCredential;
  } | null>(null);
  const history = useHistory();
  const { pathname, search } = useLocation();

  // URL is the source of truth for the toggle. Read straight off `search` so a
  // browser back/forward (or a deep-link with ?past=1) renders in sync.
  const showPastTeams = new URLSearchParams(search).get(PAST_TEAMS_PARAM) === '1';
  const setShowPastTeams = (next: boolean) => {
    const params = new URLSearchParams(search);
    if (next) params.set(PAST_TEAMS_PARAM, '1');
    else params.delete(PAST_TEAMS_PARAM);
    const qs = params.toString();
    // `replace` (not push) — a toggle flip shouldn't add a history entry the
    // user has to back through to leave the page.
    history.replace({ pathname, search: qs ? `?${qs}` : '' });
  };

  // Current year is per-render — fine for a card label, no need to memoize a
  // wall-clock value the user would have to refresh to see flip anyway.
  const currentYear = new Date().getFullYear();
  const flatPlayers = useMemo(
    () => sortPlayers(flattenGroups(groups), currentYear),
    [groups, currentYear],
  );

  if (loading && !groups.length) return <LoadingPage />;

  return (
    <FullPageLayout
      maxWidth="800px"
      parentHeader={{ title: 'My Players' }}
    >
      <ParentAccountConflictBanner
        conflicts={conflicts}
        onSwitch={(conflict, credential) => setSwitching({ conflict, credential })}
      />
      <ParentAccountSwitchDialog
        conflict={switching?.conflict ?? null}
        credential={switching?.credential ?? null}
        onClose={() => setSwitching(null)}
        onSwitched={() => setSwitching(null)}
      />
      {flatPlayers.length === 0 ? (
        <ParentDashboardEmptyState />
      ) : (
        <>
          <Box display="flex" justifyContent="flex-end" mb={1}>
            <FormControlLabel
              control={(
                <Switch
                  checked={showPastTeams}
                  onChange={(_, checked) => setShowPastTeams(checked)}
                  color="primary"
                />
              )}
              label="Show past teams"
            />
          </Box>
          {flatPlayers.map(({ displayName, profile }) => (
            <ParentPlayerCard
              key={profile._id}
              displayName={displayName}
              profile={profile}
              showPastTeams={showPastTeams}
              currentYear={currentYear}
            />
          ))}
        </>
      )}
    </FullPageLayout>
  );
};
